export const PAGE_HASH_ALIGN_GAP_PX = 40

const HEADER_TRANSITION_MS = 320
const LAZY_TARGET_TIMEOUT_MS = 8000

export function hashFromHref(href: string) {
  try {
    return new URL(href, window.location.origin).hash
  } catch {
    return href.startsWith('#') ? href : ''
  }
}

function pageHashId(href: string) {
  const hash = hashFromHref(href)
  return hash ? hash.slice(1) : ''
}

export function hashAlignTarget(root: HTMLElement) {
  return root.querySelector<HTMLElement>('[data-nav-align]') ?? root
}

export function headerClearancePx() {
  const bar = document.querySelector<HTMLElement>('[data-site-header-bar]')
  return (bar?.offsetHeight ?? 0) + PAGE_HASH_ALIGN_GAP_PX
}

function applyPageHashScroll(href: string) {
  const id = pageHashId(href)
  if (!id) return false
  if (id === 'top') {
    window.scrollTo({ top: 0 })
    return true
  }

  const target = document.getElementById(id)
  if (!target) return false

  const align = hashAlignTarget(target)
  const top =
    window.scrollY + align.getBoundingClientRect().top - headerClearancePx()
  window.scrollTo({ top: Math.max(0, top) })
  return true
}

let retryTimer = 0
let retryRaf1 = 0
let retryRaf2 = 0
let lazyTimeout = 0
let lazyObserver: MutationObserver | null = null

function cancelHashScrollRetries() {
  if (retryTimer) window.clearTimeout(retryTimer)
  if (retryRaf1) window.cancelAnimationFrame(retryRaf1)
  if (retryRaf2) window.cancelAnimationFrame(retryRaf2)
  if (lazyTimeout) window.clearTimeout(lazyTimeout)
  lazyObserver?.disconnect()
  retryTimer = 0
  retryRaf1 = 0
  retryRaf2 = 0
  lazyTimeout = 0
  lazyObserver = null
}

function scheduleHashScrollSettles(href: string) {
  retryRaf1 = window.requestAnimationFrame(() => {
    applyPageHashScroll(href)
    retryRaf2 = window.requestAnimationFrame(() => {
      applyPageHashScroll(href)
    })
  })
  retryTimer = window.setTimeout(() => {
    applyPageHashScroll(href)
  }, HEADER_TRANSITION_MS)
}

function waitForHashTarget(href: string) {
  const id = pageHashId(href)
  if (!id || id === 'top') return

  lazyObserver = new MutationObserver(() => {
    if (!document.getElementById(id)) return
    lazyObserver?.disconnect()
    lazyObserver = null
    if (lazyTimeout) window.clearTimeout(lazyTimeout)
    lazyTimeout = 0
    applyPageHashScroll(href)
    scheduleHashScrollSettles(href)
  })
  lazyObserver.observe(document.body, { childList: true, subtree: true })
  lazyTimeout = window.setTimeout(() => {
    lazyObserver?.disconnect()
    lazyObserver = null
    lazyTimeout = 0
  }, LAZY_TARGET_TIMEOUT_MS)
}

/**
 * Scrolls a hash target so its `[data-nav-align]` intro (or the element itself)
 * sits just below the sticky site header. Safe to call while `document.body`
 * overflow is locked (mobile nav).
 */
export function scrollToPageHash(href: string) {
  cancelHashScrollRetries()
  const applied = applyPageHashScroll(href)
  if (!applied) {
    waitForHashTarget(href)
    return false
  }
  scheduleHashScrollSettles(href)
  return true
}

export function syncPageHash(href: string) {
  const hash = hashFromHref(href)
  if (!hash || window.location.hash === hash) return
  const next = `${window.location.pathname}${window.location.search}${hash}`
  window.history.pushState(null, '', next)
}

/** Set while the mobile menu is open; flushed when body scroll unlocks. */
let pendingHashHref: string | null = null

/** Scroll now (while locked) and again after the overlay restores body scroll. */
export function queuePageHashScroll(href: string) {
  pendingHashHref = href
  scrollToPageHash(href)
}

/** Re-apply a queued hash scroll after body overflow unlock (mobile Safari). */
export function flushPendingPageHashScroll() {
  if (!pendingHashHref) return
  const href = pendingHashHref
  pendingHashHref = null
  scrollToPageHash(href)
}
